/**
 * QuestActsPanel — Section "Actes (MJ)" sur la page détail d'une quête.
 *
 * Visible et accessible uniquement par les MJ.
 * Chaque acte est affiché en accordéon : titre + contenu markdown.
 * Fonctionnalités : ajout, édition (markdown), suppression, réordonnancement ↑↓.
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { t } from '../../lib/i18n';
import {
  Plus,
  Loader2,
  Trash2,
  Pencil,
  Save,
  X,
  ChevronUp,
  ChevronDown,
  BookOpen,
  EyeOff,
} from 'lucide-react';

const API_URL = '/api';

// ── Rendu markdown minimal (sans dépendance externe) ─────────────────────
function SimpleMarkdown({ content }) {
  if (!content?.trim()) {
    return <p className="text-sm text-muted-foreground italic">—</p>;
  }

  const inlineMarkdown = (text) =>
    text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/\*\*\*(.+?)\*\*\*/g, '<strong><em>$1</em></strong>')
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.+?)\*/g, '<em>$1</em>')
      .replace(/_(.+?)_/g, '<em>$1</em>')
      .replace(/`(.+?)`/g, '<code class="bg-muted px-1 rounded text-xs font-mono">$1</code>');

  const lines = content.split('\n');
  const elements = [];
  let listBuffer = [];
  let key = 0;

  const flushList = () => {
    if (!listBuffer.length) return;
    elements.push(
      <ul key={key++} className="list-disc pl-5 space-y-0.5 my-1.5">
        {listBuffer.map((item, j) => (
          <li key={j} className="text-sm leading-relaxed"
            dangerouslySetInnerHTML={{ __html: inlineMarkdown(item) }} />
        ))}
      </ul>
    );
    listBuffer = [];
  };

  for (const raw of lines) {
    const line = raw;

    if (/^#{3}\s/.test(line)) {
      flushList();
      elements.push(
        <h3 key={key++} className="font-cinzel font-bold text-sm mt-3 mb-1 text-foreground">
          {line.slice(4)}
        </h3>
      );
    } else if (/^#{2}\s/.test(line)) {
      flushList();
      elements.push(
        <h2 key={key++} className="font-cinzel font-bold text-base mt-4 mb-1.5 text-foreground">
          {line.slice(3)}
        </h2>
      );
    } else if (/^#\s/.test(line)) {
      flushList();
      elements.push(
        <h1 key={key++} className="font-cinzel font-bold text-lg mt-4 mb-2 text-foreground">
          {line.slice(2)}
        </h1>
      );
    } else if (/^[-*]\s/.test(line)) {
      listBuffer.push(line.slice(2));
    } else if (/^>\s/.test(line)) {
      flushList();
      elements.push(
        <blockquote key={key++}
          className="border-l-2 border-primary/40 pl-3 my-1.5 italic text-muted-foreground text-sm"
          dangerouslySetInnerHTML={{ __html: inlineMarkdown(line.slice(2)) }}
        />
      );
    } else if (/^---+$/.test(line.trim())) {
      flushList();
      elements.push(<hr key={key++} className="border-border my-3" />);
    } else if (line.trim() === '') {
      flushList();
      if (elements.length) elements.push(<div key={key++} className="h-1.5" />);
    } else {
      flushList();
      elements.push(
        <p key={key++} className="text-sm leading-relaxed"
          dangerouslySetInnerHTML={{ __html: inlineMarkdown(line) }}
        />
      );
    }
  }
  flushList();

  return <div className="space-y-0.5 prose-sm">{elements}</div>;
}

// ── ActCard ───────────────────────────────────────────────────────────────
function ActCard({ act, index, total, language, onUpdate, onDelete, onMoveUp, onMoveDown }) {
  const [open,    setOpen]    = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving,  setSaving]  = useState(false);
  const [draft,   setDraft]   = useState({ title: act.title, content: act.content });
  const [preview, setPreview] = useState(false);

  // Réinitialiser le brouillon si l'acte change de l'extérieur
  useEffect(() => {
    setDraft({ title: act.title, content: act.content });
  }, [act.title, act.content]);

  const handleSave = async () => {
    if (!draft.title.trim()) {
      toast.error(language === 'fr' ? 'Le titre est requis' : 'Title is required');
      return;
    }
    setSaving(true);
    try {
      const res = await axios.patch(`${API_URL}/gm/acts/${act.id}`, {
        title:   draft.title.trim(),
        content: draft.content,
      });
      onUpdate(res.data);
      setEditing(false);
      toast.success(t('success_saved', language));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('error_generic', language));
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setDraft({ title: act.title, content: act.content });
    setEditing(false);
    setPreview(false);
  };

  const handleDelete = async () => {
    const msg = language === 'fr'
      ? `Supprimer "${act.title}" ?`
      : `Delete "${act.title}"?`;
    if (!window.confirm(msg)) return;
    try {
      await axios.delete(`${API_URL}/gm/acts/${act.id}`);
      onDelete(act.id);
      toast.success(t('success_deleted', language));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('error_generic', language));
    }
  };

  const actLabel = language === 'fr' ? `Acte ${index + 1}` : `Act ${index + 1}`;

  return (
    <Card className="glass-card border-primary/20">
      {/* ── Titre / Header ─────────────────────────────────────────────── */}
      <div className="flex items-center gap-2 px-4 py-3">
        {/* Boutons réorder */}
        <div className="flex flex-col gap-0.5 shrink-0">
          <button
            onClick={onMoveUp}
            disabled={index === 0}
            className="text-muted-foreground hover:text-foreground disabled:opacity-20 disabled:cursor-not-allowed"
            title={language === 'fr' ? 'Monter' : 'Move up'}
          >
            <ChevronUp className="h-3.5 w-3.5" />
          </button>
          <button
            onClick={onMoveDown}
            disabled={index === total - 1}
            className="text-muted-foreground hover:text-foreground disabled:opacity-20 disabled:cursor-not-allowed"
            title={language === 'fr' ? 'Descendre' : 'Move down'}
          >
            <ChevronDown className="h-3.5 w-3.5" />
          </button>
        </div>

        {/* Numéro */}
        <Badge variant="outline" className="text-xs shrink-0 font-mono">
          {actLabel}
        </Badge>

        {/* Titre (éditable en mode édition, sinon toggle accordéon) */}
        {editing ? (
          <Input
            value={draft.title}
            onChange={e => setDraft(d => ({ ...d, title: e.target.value }))}
            className="h-7 text-sm font-semibold flex-1"
            autoFocus
            onKeyDown={e => e.key === 'Enter' && handleSave()}
          />
        ) : (
          <button
            className="flex-1 text-left text-sm font-semibold hover:text-primary transition-colors truncate"
            onClick={() => { if (!editing) setOpen(o => !o); }}
          >
            {act.title}
          </button>
        )}

        {/* Actions */}
        <div className="flex items-center gap-1 shrink-0">
          {editing ? (
            <>
              <Button
                size="sm" variant="ghost"
                className="h-7 w-7 p-0"
                title={language === 'fr' ? 'Aperçu' : 'Preview'}
                onClick={() => setPreview(p => !p)}
              >
                <BookOpen className={`h-3.5 w-3.5 ${preview ? 'text-primary' : ''}`} />
              </Button>
              <Button
                size="sm" variant="ghost"
                className="h-7 px-2 text-xs"
                onClick={handleCancelEdit}
              >
                <X className="h-3.5 w-3.5 mr-1" />
                {t('cancel', language)}
              </Button>
              <Button
                size="sm"
                className="h-7 px-2 text-xs"
                onClick={handleSave}
                disabled={saving}
              >
                {saving
                  ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
                  : <Save    className="h-3.5 w-3.5 mr-1" />}
                {t('save', language)}
              </Button>
            </>
          ) : (
            <>
              <Button
                size="sm" variant="ghost"
                className="h-7 w-7 p-0"
                title={t('edit', language)}
                onClick={() => { setOpen(true); setEditing(true); }}
              >
                <Pencil className="h-3.5 w-3.5" />
              </Button>
              <Button
                size="sm" variant="ghost"
                className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                title={t('delete', language)}
                onClick={handleDelete}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </>
          )}
        </div>
      </div>

      {/* ── Contenu accordéon ──────────────────────────────────────────── */}
      {(open || editing) && (
        <CardContent className="pt-0 pb-4 px-4 border-t border-border/40">
          {editing ? (
            <div className="space-y-2 mt-3">
              {preview ? (
                <div className="min-h-[120px] rounded-md border border-border/50 bg-muted/20 p-3">
                  <SimpleMarkdown content={draft.content} />
                </div>
              ) : (
                <Textarea
                  value={draft.content}
                  onChange={e => setDraft(d => ({ ...d, content: e.target.value }))}
                  placeholder={language === 'fr'
                    ? 'Contenu de l\'acte (markdown supporté)…\n\n## Scène 1\n**Ambiance** : …\n- NPC présents : …'
                    : 'Act content (markdown supported)…\n\n## Scene 1\n**Atmosphere**: …\n- NPCs present: …'}
                  rows={10}
                  className="text-sm font-mono resize-y"
                />
              )}
              <p className="text-xs text-muted-foreground">
                {language === 'fr'
                  ? 'Markdown : **gras**, *italique*, ## Titre, - liste, > citation'
                  : 'Markdown: **bold**, *italic*, ## Heading, - list, > quote'}
              </p>
            </div>
          ) : (
            <div className="mt-3">
              <SimpleMarkdown content={act.content} />
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}

// ── Panneau principal ─────────────────────────────────────────────────────
export default function QuestActsPanel({ questId, language }) {
  const [acts,    setActs]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding,  setAdding]  = useState(false);
  const [newTitle, setNewTitle] = useState('');

  // ── Chargement ──────────────────────────────────────────────────────
  const loadActs = useCallback(async () => {
    if (!questId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/gm/quests/${questId}/acts`);
      setActs(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('error_generic', language));
    } finally {
      setLoading(false);
    }
  }, [questId, language]);

  useEffect(() => { loadActs(); }, [loadActs]);

  // ── Créer un acte ───────────────────────────────────────────────────
  const handleAdd = async () => {
    const title = newTitle.trim()
      || (language === 'fr' ? `Acte ${acts.length + 1}` : `Act ${acts.length + 1}`);
    setAdding(true);
    try {
      const res = await axios.post(`${API_URL}/gm/quests/${questId}/acts`, {
        title,
        content: '',
      });
      setActs(prev => [...prev, res.data]);
      setNewTitle('');
      toast.success(language === 'fr' ? 'Acte ajouté' : 'Act added');
    } catch (err) {
      toast.error(err.response?.data?.detail || t('error_generic', language));
    } finally {
      setAdding(false);
    }
  };

  // ── Mise à jour locale ──────────────────────────────────────────────
  const handleUpdate = (updated) => {
    setActs(prev => prev.map(a => a.id === updated.id ? updated : a));
  };

  const handleDelete = (deletedId) => {
    setActs(prev => {
      const next = prev.filter(a => a.id !== deletedId);
      // Mise à jour des order_index locaux
      return next.map((a, i) => ({ ...a, order_index: i }));
    });
  };

  // ── Réordonnancement ────────────────────────────────────────────────
  const moveAct = async (fromIndex, toIndex) => {
    if (toIndex < 0 || toIndex >= acts.length) return;
    const reordered = [...acts];
    const [moved] = reordered.splice(fromIndex, 1);
    reordered.splice(toIndex, 0, moved);
    const withIdx = reordered.map((a, i) => ({ ...a, order_index: i }));
    setActs(withIdx); // optimistic update

    try {
      await axios.patch(`${API_URL}/gm/quests/${questId}/acts/reorder`, {
        act_ids: withIdx.map(a => a.id),
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || t('error_generic', language));
      loadActs(); // rollback
    }
  };

  return (
    <div className="space-y-3">
      {/* ── Header ──────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <EyeOff className="h-4 w-4 text-primary" />
          <h3 className="font-cinzel font-semibold text-base">
            {language === 'fr' ? 'Actes (MJ)' : 'Acts (GM)'}
          </h3>
          {acts.length > 0 && (
            <Badge variant="secondary" className="text-xs">
              {acts.length}
            </Badge>
          )}
        </div>
        <p className="text-xs text-muted-foreground">
          {language === 'fr' ? 'Visible uniquement par le MJ' : 'Visible to GM only'}
        </p>
      </div>

      {/* ── Liste des actes ─────────────────────────────────────────── */}
      {loading ? (
        <div className="flex items-center gap-2 py-6 justify-center text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span className="text-sm">{t('loading', language)}</span>
        </div>
      ) : acts.length === 0 ? (
        <div className="text-center py-6 rounded-lg border border-dashed border-border/60">
          <BookOpen className="h-7 w-7 mx-auto mb-2 text-muted-foreground/40" />
          <p className="text-sm text-muted-foreground">
            {language === 'fr'
              ? 'Aucun acte défini. Découpez votre scénarisation ici.'
              : 'No acts defined. Break down your plot structure here.'}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {acts.map((act, i) => (
            <ActCard
              key={act.id}
              act={act}
              index={i}
              total={acts.length}
              language={language}
              onUpdate={handleUpdate}
              onDelete={handleDelete}
              onMoveUp={()   => moveAct(i, i - 1)}
              onMoveDown={() => moveAct(i, i + 1)}
            />
          ))}
        </div>
      )}

      {/* ── Ajouter un acte ─────────────────────────────────────────── */}
      <div className="flex items-center gap-2 pt-1">
        <Input
          placeholder={language === 'fr'
            ? `Acte ${acts.length + 1} — titre (optionnel)…`
            : `Act ${acts.length + 1} — title (optional)…`}
          value={newTitle}
          onChange={e => setNewTitle(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && !adding && handleAdd()}
          className="h-8 text-sm flex-1"
          disabled={adding}
        />
        <Button
          size="sm"
          variant="outline"
          onClick={handleAdd}
          disabled={adding}
          className="h-8 px-3 shrink-0"
        >
          {adding
            ? <Loader2 className="h-4 w-4 animate-spin" />
            : <><Plus className="h-4 w-4 mr-1" />{language === 'fr' ? 'Ajouter' : 'Add'}</>}
        </Button>
      </div>
    </div>
  );
}
